import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { Specialty } from "../../types/specialty.definition";

interface SpecialtiesState {
  specialties: Specialty[];
  loading: boolean;
  error: string | null;
}

const initialState: SpecialtiesState = {
  specialties: [],
  loading: false,
  error: null,
};

const specialtiesSlice = createSlice({
  name: "specialties",
  initialState,
  reducers: {
    getSpecialtiesRequest: state => {
      state.loading = true;
      state.error = null;
    },
    getSpecialtiesSuccess: (state, action: PayloadAction<Specialty[]>) => {
      state.loading = false;
      // Labels arrive translated for the current language: sort them once here
      // so every specialty dropdown lists them alphabetically.
      // A specialty missing its translation arrives with an empty label: show
      // its code instead (as getLabelByCode does), otherwise the dropdown
      // renders an unlabeled option that can never appear checked.
      state.specialties = action.payload
        .map(specialty =>
          specialty.label ? specialty : { ...specialty, label: specialty.code }
        )
        .sort((a, b) =>
          a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
        );
      state.error = null;
    },
    getSpecialtiesFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    clearSpecialties: state => {
      state.specialties = [];
      state.error = null;
    },
  },
});

export const {
  getSpecialtiesRequest,
  getSpecialtiesSuccess,
  getSpecialtiesFailure,
  clearSpecialties,
} = specialtiesSlice.actions;

// Selectors
export const selectSpecialties = (state: { specialties: SpecialtiesState }) =>
  state.specialties.specialties;

export const selectSpecialtiesLoading = (state: {
  specialties: SpecialtiesState;
}) => state.specialties.loading;

export const selectSpecialtiesError = (state: {
  specialties: SpecialtiesState;
}) => state.specialties.error;

export default specialtiesSlice.reducer;
